'use client';

import React from 'react';
import { MainSectionLayout } from '@/features/client/MainSectionLayout/MainSectionLayout';
import { MainSection } from '@/features/client/MainSection/MainSection';
import { Container } from '@/components/client/Container/Container';
import { Section } from '@/components/client/Section/Section';
import { ContactUsBanner } from '@/features/client/ContactUsBanner/ContactUsBanner';
import { ServiceCardsSection } from '@/features/client/ServiceCardsSection/ServiceCardsSection';
import { ContentLayout } from '@/features/client/Services/components/ContentLayout/ContentLayout';
import { RequestForm } from '@/features/client/Forms/RequestForm/RequestForm';
import { ServicesNavMenu } from '@/features/client/Services/components/ServicesNavMenu/ServicesNavMenu';
import { Content } from '@/features/client/Services/components/Content/Content';
import image from '@/public/images/services/main_page/new_construction.webp';
const data = {
  title: 'Construction Expertise',
  description: `At Tytum, we're dedicated to innovation and your home comfort. We aim to keep you comfortable year-round with energy-efficient and built to last solutions.\n\nWith extensive experience in new construction, we provide a comprehensive range of services to bring your residential and commercial projects to life. Our dedicated team handles every detail, ensuring quality and precision from start to finish.`,
  image: image,
  offer_title: 'New Construction Services We Offer:',
  offer_data: [
    {
      title: 'Residential New Construction:',
      list: [
        { title: '    Custom Home Building' },
        { title: 'Single-Family Home Construction' },
        { title: 'Multi-Family Home Construction' },
        { title: 'Design-Build Services' },
        { title: 'Site Preparation and Grading' },
        { title: 'Foundation Laying and Concrete Work' },
        { title: 'Framing and Structural Work' },
        { title: 'Roofing Installation' },
        { title: 'Siding Installation' },
        { title: 'Window and Door Installation' },
        { title: 'Insulation Installation' },
      ],
    },

    {
      title: 'Home Additions:  ',
      list: [
        { title: '    Room Additions' },
        { title: 'Second Story Additions' },
        { title: 'Garage Construction' },
        { title: 'In-Law Suites and Guest Houses' },
        {
          title:
            'Sunroom Additions: all projects seamlessly connected with our <a href="https://tytum.com/services/remodeling">remodeling services</a>, so that every new space feels fully integrated into your home.',
        },
      ],
    },

    {
      title: 'Interior Finishing:   ',
      list: [
        { title: '    Drywall Installation and Finishing' },
        { title: 'Flooring Installation (Hardwood, Tile, Carpet)' },
        { title: 'Custom Cabinetry and Built-Ins' },
        { title: 'Painting and Wallpapering' },
        { title: 'Trim and Molding Installation' },
        { title: 'Staircase Design and Installation' },
      ],
    },

    {
      title: 'Exterior Finishing:   ',
      list: [
        {
          title: '    Exterior Siding Installation (Vinyl, Wood, Fiber Cement)',
        },
        { title: 'Masonry and Stonework' },
        { title: 'Deck and Patio Construction' },
        { title: 'Porch and Balcony Construction' },
        { title: 'Exterior Painting' },
        { title: 'Landscape Design and Installation' },
      ],
    },

    {
      title: 'Plumbing and Electrical Systems:   ',
      list: [
        {
          title:
            'New Plumbing System Installation: backed by our full range of professional <a href="https://tytum.com/services/plumbing">plumbing services</a> to ensure reliability and long-term performance.',
        },
        { title: 'Sewer and Water Line Installation' },
        { title: 'New Electrical Wiring and Panel Installation' },
        { title: 'Lighting Design and Installation' },
        { title: 'HVAC System Installation' },
      ],
    },

    {
      title: 'Energy-Efficient Construction:   ',
      list: [
        { title: '    Green Building Techniques' },
        { title: 'Energy-Efficient Windows and Doors' },
        { title: 'Solar Panel Installation' },
        { title: 'Insulation and Weatherproofing' },
        { title: 'Smart Home Integration' },
      ],
    },

    {
      title: 'Project Management:    ',
      list: [
        { title: '    Comprehensive Project Planning' },
        { title: 'Budgeting and Cost Estimation' },
        { title: 'Scheduling and Timeline Management' },
        { title: 'Coordination with Architects and Designers' },
        { title: 'Permitting and Code Compliance' },
      ],
    },

    {
      title: 'Luxury Home Features:   ',
      list: [
        { title: '    Home Theater Installation' },
        { title: 'Custom Wine Cellars' },
        { title: 'Spa and Sauna Installation' },
        { title: 'Home Gym Construction' },
        { title: 'High-End Finishes and Materials' },
      ],
    },
  ],
  banner_title: `“Building Your Vision from the Ground Up”`,
  banner_description:
    'Tytum’s new construction services bring your dream home to life. We handle every detail with expert care, ensuring quality and satisfaction in every phase.',
  bookServiceButton: true,
  why_title: 'Why Choose Tytum for New Construction Solutions?',
  why_description:
    'We know how crucial it is to promptly and effectively address problems to ensure the safety and functionality of your home. We deliver quality results on time and within budget, turning your dream home into a reality.',
  why_items: [
    {
      title: 'Custom Craftsmanship: ',
      description:
        'We build homes tailored to your vision, ensuring quality and attention to detail in every project.',
    },
    {
      title: 'End-to-End Service:  ',
      description:
        'From initial concept to final inspection, we manage every aspect of your new construction project.',
    },
    {
      title: 'Sustainable Practices:',
      description:
        'We prioritize energy-efficient and environmentally-friendly construction methods.',
    },
  ],
  why_footerText:
    'Trust us to be your go-to provider in the San Diego area and ensure the comfort and safety of your home. For homeowners planning to sell or invest, pairing new builds with <a href="https://tytum.com/services/brokerage">real estate brokerage services</a> can maximize both comfort and property value. Call us or fill out our online form for questions or a free quote.',
};
const NewConstructionPage = () => {
  return (
    <div>
      <MainSectionLayout variant="primary" layout="line">
        <MainSection
          label="Service Details"
          title="New Construction"
          description="Transform your vision into reality with our expert new construction services.
From custom homes to functional spaces, we ensure every project meets your highest
standards and expectations."
          withContactButton
        />
      </MainSectionLayout>

      <Section variant="secondary">
        <Container>
          <ContentLayout
            contentLeft={
              <>
                <ServicesNavMenu />
                <RequestForm />
              </>
            }
            contentRight={<Content data={data} />}
          />
        </Container>
      </Section>

      <Section variant="secondary">
        <Container>
          <ServiceCardsSection shortVariant={true} />
        </Container>
      </Section>

      <Section variant="secondary">
        <Container>
          <ContactUsBanner phone="+1(858) 289-4881" />
        </Container>
      </Section>
    </div>
  );
};

export default NewConstructionPage;
